import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { errorText } from "../../api/client";
import { deleteMe } from "../../api/me";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Field, FormError } from "../../components/Field";
import { nl } from "../../i18n/nl";
import { PRIVACY_PATH } from "../../routes/screens";
import styles from "./profile.module.css";

const t = nl.profile.delete;

/** Deleting cannot be undone, so this is one of the few places with a confirmation dialog. */
export function DeleteAccount({ email }: { email: string }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const remove = useMutation({
    mutationFn: () => deleteMe(typed.trim()),
    onSuccess: () => {
      client.clear();
      void navigate(`${PRIVACY_PATH}?verwijderd=1`, { replace: true });
    },
  });
  const matches = typed.trim().toLowerCase() === email.toLowerCase();

  return (
    <section className={styles.card} aria-labelledby="delete-title">
      <h2 id="delete-title" className={styles.sectionTitle}>
        {t.title}
      </h2>
      <p className={styles.help}>{t.lead}</p>
      <div className={styles.row}>
        <Button
          className={styles.danger}
          onClick={() => {
            setOpen(true);
          }}
        >
          {t.open}
        </Button>
        <Link to={PRIVACY_PATH}>{t.privacyLink}</Link>
      </div>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setTyped("");
        }}
        title={t.confirmTitle}
        description={t.confirmText}
      >
        <form
          className={styles.dialogForm}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (matches) remove.mutate();
          }}
        >
          <ul className={styles.deleteList}>
            {t.what.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <Field
            label={t.typeEmail(email)}
            type="email"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
          <FormError message={remove.error ? errorText(remove.error) : null} />
          <div className={styles.row}>
            <Button
              onClick={() => {
                setOpen(false);
                setTyped("");
              }}
            >
              {t.cancel}
            </Button>
            <Button type="submit" variant="primary" disabled={!matches || remove.isPending}>
              {remove.isPending ? nl.auth.working : t.confirm}
            </Button>
          </div>
        </form>
      </Dialog>
    </section>
  );
}
